import { RefObject, KeyboardEvent } from 'react';
class Formatter {
  constructor({
    initialValue = '',
    maskCharacter,
    pattern = [],
    refInput
  }) {
    this.maskCharacter = maskCharacter;
    this.pattern = pattern;
    this.refInput = refInput;
    this._currentCaretPos = [];
    this._blanks = this._init(initialValue);
    this._key = '';
    this._currentShift = 0;
    this.value = this._blanks.map(part => part.value).join('');
    this.unmaskedValue = initialValue;
  }
  _init(initialValue) {
    if (this.pattern?.length === 0) return [];
    let startIndex = 0;
    let token = initialValue;
    return this.pattern?.reduce((acc, cur, i) => {
      const prevShift = i !== 0 ? acc[i - 1].nextShift : 0;
      if (typeof cur === 'string') {
        startIndex = startIndex + cur.length;
        return [...acc, {
          value: cur,
          nextShift: prevShift
        }];
      }
      const hasBounds = cur.source.match(/{(.*?)}/);
      const regex = hasBounds && !hasBounds[1].includes(',') ? new RegExp(cur.source.replace('{', '{1,')) : cur;
      const minBound = hasBounds ? Math.min(...hasBounds[1].split(',')) : 1;
      const maxBound = hasBounds && hasBounds[1].includes(',') ? Math.max(...hasBounds[1].split(',')) : minBound;
      const initialPart = token.slice(0, maxBound);
      token = token.slice(maxBound) || '';
      const endIndex = startIndex + Math.max(initialPart.length, minBound);
      const value = `${initialPart}${[...Array(10).keys()].map(() => this.maskCharacter).join('')}`.substring(0, Math.max(initialPart.length, minBound));
      const blank = [...acc, {
        indexPositions: [startIndex, endIndex],
        maxBound,
        minBound,
        nextShift: maxBound !== minBound ? Math.min(maxBound, initialValue.length) - minBound + prevShift : 0,
        regex: regex,
        unmaskedValue: value.replace(new RegExp(`${this.maskCharacter}+`), ''),
        value
      }];
      startIndex = endIndex;
      return blank;
    }, []);
  }
  _updateBlanks() {
    if (this._blanks?.length === 0) return [];
    const startCaretCurrentBound = this._currentCaretPos[0];
    const endCaretCurrentBound = this._currentCaretPos[1];
    let token = this._key || '';
    return this._blanks = this._blanks.reduce((acc, blank, i, arr) => {
      if (!blank) return acc;
      const {
        indexPositions,
        maxBound,
        minBound,
        regex,
        unmaskedValue,
        value
      } = blank;
      const prevShift = i !== 0 ? acc[i - 1].nextShift : 0;
      const hasIntersection = !(indexPositions && (indexPositions[0] + prevShift > endCaretCurrentBound || indexPositions[1] + prevShift + Number(this._key === 'Backspace') < startCaretCurrentBound));
      const isValidKey = blank?.regex && !['Backspace', 'Delete'].includes(this._key) && !regex.test(token) && token !== '';
      if (!regex || !hasIntersection || this._key === ' ' || isValidKey) {
        return [...acc, {
          ...blank,
          nextShift: prevShift
        }];
      }
      const isRange = startCaretCurrentBound !== endCaretCurrentBound;
      const startBound = startCaretCurrentBound <= indexPositions[0] ? 0 : startCaretCurrentBound - indexPositions[0];
      let endBound = startBound;
      let blankRange = 1;
      if (startCaretCurrentBound !== endCaretCurrentBound) {
        endBound = endCaretCurrentBound >= indexPositions[1] ? indexPositions[1] - indexPositions[0] : indexPositions[1] - endCaretCurrentBound;
        blankRange = endBound - startBound;
      }
      let valuePart;
      if (this._key === 'Backspace') {
        const fillerAmount = minBound > unmaskedValue.length - blankRange && (startCaretCurrentBound !== indexPositions[0] || isRange);
        const filler = fillerAmount ? [...Array(Math.min(blankRange, minBound)).keys()].map(() => this.maskCharacter).join('') : '';
        if (maxBound !== minBound) {
          this._currentShift = this._currentShift - blankRange;
        }
        const firstPart = value.substring(0, isRange ? startBound : startBound - 1) + filler;
        valuePart = firstPart + value.substring(endBound, isRange ? endBound : undefined);
        let caretPos = !isRange ? startCaretCurrentBound - 1 : startCaretCurrentBound;
        if (!isRange) {
          if (!(startBound > 1)) {
            caretPos = indexPositions[0];
            const remainder = arr.slice(0, i).filter(blank => blank.regex).reverse();
            const prevBlank = remainder.find(blank => blank.indexPositions[0] <= startCaretCurrentBound);
            if (prevBlank) {
              caretPos = prevBlank.indexPositions[1];
            }
          }
        } else {
          const remainder = arr.slice(0, i).filter(blank => blank.regex).reverse();
          const prevBlank = remainder.find(blank => blank.indexPositions[0] < startCaretCurrentBound);
          if (prevBlank) {
            caretPos = prevBlank.indexPositions[1] - blankRange;
          }
        }
        this._currentCaretPos = [caretPos, caretPos];
      }
      if (this._key === 'Delete') {
        const fillerAmount = minBound > unmaskedValue.length - blankRange;
        const filler = fillerAmount ? [...Array(Math.min(blankRange, minBound)).keys()].map(() => this.maskCharacter).join('') : '';
        if (maxBound !== minBound && maxBound !== endBound) {
          this._currentShift = this._currentShift - blankRange;
        }
        const firstPart = value.substring(0, startBound) + filler;
        valuePart = firstPart + value.substring(endBound + 1, isRange ? endBound : undefined);
        let caretPos = !isRange ? startCaretCurrentBound + 1 : startCaretCurrentBound;
        if (startBound !== maxBound - 1 && endBound !== maxBound) {
          caretPos = startCaretCurrentBound + 1;
        } else {
          const remainder = arr.slice(i + 1).filter(blank => blank.regex);
          const nextBlank = remainder.find(blank => blank.regex);
          if (nextBlank) {
            caretPos = nextBlank.indexPositions[0];
          } else if (maxBound !== endBound) {
            caretPos = startCaretCurrentBound;
          }
        }
        this._currentCaretPos = [caretPos, caretPos];
      }
      if (this._key !== 'Backspace' && this._key !== 'Delete' && (blank.regex.test(token) || token === '')) {
        let filler = token.slice(0, maxBound - startBound);
        token = token.slice(maxBound - startBound) || '';
        if (maxBound !== minBound && maxBound !== endBound) {
          this._currentShift = i !== 0 ? arr[i - 1].nextShift + this._currentShift + filler?.length : this._currentShift + filler?.length;
        }
        const firstPart = value.substring(0, startBound) + filler;
        valuePart = firstPart + (this._key?.length === 1 ? value.substring(isRange ? endBound : endBound + 1) : '') + (isRange && minBound > firstPart.length ? [...Array(minBound - filler.length).keys()].map(() => this.maskCharacter).join('') : '');
        let caretPos = startCaretCurrentBound;
        if (startBound !== maxBound - 1 && endBound !== maxBound) {
          caretPos = startCaretCurrentBound + filler?.length;
        } else {
          const remainder = arr.slice(i + 1).filter(blank => blank.regex);
          const nextBlank = remainder.find(blank => blank.regex);
          if (nextBlank) {
            caretPos = nextBlank.indexPositions[0];
          } else if (maxBound !== endBound) {
            caretPos = startCaretCurrentBound + filler?.length;
          }
        }
        this._currentCaretPos = [caretPos, caretPos];
      }
      let nextShift = 0;
      let _indexPositions = indexPositions;
      if (maxBound !== minBound) {
        nextShift = this._currentShift < 0 ? maxBound - minBound + this._currentShift : minBound + this._currentShift;
        _indexPositions = [prevShift ? indexPositions[0] + this._currentShift : indexPositions[0], indexPositions[1] + Math.max(this._currentShift, minBound)];
      }
      return [...acc, {
        ...blank,
        indexPositions: _indexPositions,
        value: valuePart,
        nextShift,
        unmaskedValue: valuePart?.replace(new RegExp(`${this.maskCharacter}+`), '')
      }];
    }, []);
  }
  _getValues() {
    if (this._blanks.length === 0) return [];
    return this._blanks.reduce((acc, cur) => {
      return [acc[0] + cur.value, acc[1] + (cur.unmaskedValue || '')];
    }, ['', '']);
  }
  keyDown(e) {
    this._key = e.key || '';
    if (process.env.NODE_ENV !== 'test' || process.env.NODE_ENV === 'test' && this._currentCaretPos.length === 0) {
      const {
        current
      } = this.refInput;
      const inputSelectionStart = current?.selectionStart || 0;
      const inputSelectionEnd = current?.selectionEnd || 0;
      this._currentCaretPos = [inputSelectionStart, inputSelectionEnd];
    }
  }
  beforeInput(e) {
    this._key = e.data || '';
  }
  input() {
    const {
      current
    } = this.refInput;
    this._updateBlanks();
    const [val, unmaskedVal] = this._getValues();
    Promise.resolve().then(() => {
      current?.setSelectionRange(this._currentCaretPos[0], this._currentCaretPos[0], 'none');
    });
    this.value = val;
    this.unmaskedValue = unmaskedVal;
    if (current) {
      current.setAttribute('data-unmasked-value', this.unmaskedValue);
      current.value = this.value;
    }
    return [this.value, this.unmaskedValue];
  }
  set(value) {
    const {
      current
    } = this.refInput;
    if (value !== undefined) {
      this._key = value;
      this._currentShift = value.length - 1;
      const bounds = this._blanks.find(item => item.indexPositions)?.indexPositions || [0, 0];
      this._currentCaretPos = [bounds[0], bounds[1] + this._currentShift - 1];
    }
    this._updateBlanks();
    const [val, unmaskedVal] = this._getValues();
    this.value = val;
    this.unmaskedValue = unmaskedVal;
    if (current) {
      current.setAttribute('data-unmasked-value', this.unmaskedValue);
      current.value = this.value;
    }
    return [this.value, this.unmaskedValue];
  }
}
export default Formatter;